import { GtmPlugin } from '@gtm-support/vue-gtm';
import { GTM_STRINGS } from '../config/gtm-strings';

type Brand = {
  brand: string;
};

type VariantPrice = {
  netPrice: string;
  salesPrice: string;
  alloyingPrice: string;
};

type Breadcrumb = {
  parents: {
    name: string;
  }[];
};

type Product = {
  name: string;
  categoryPaths: string[];
  id: string;
  breadcrumb: Breadcrumb;
  // eslint-disable-next-line no-use-before-define
  variants: Variant[];
  // eslint-disable-next-line no-use-before-define
  requestedVariant: Variant;
  listPosition: number;
};

type Variant = {
  articleNumber: string;
  name: string;
  product: Product;
  brands: Brand[];
  price: VariantPrice;
  quantity: number;
  listPosition: string;
};

type CartItem = {
  product: Product;
  articleNumber: string;
  price: string;
  alloyingPrice: string;
  brands: Brand[];
  quantity: number;
  name: string;
  salesPrice: string;
};

type Banner = {
  marketingId: string;
  metaTitle: string;
  creative: string;
};

type FormattedCartItem = {
  name: string;
  id: string;
  price: string;
  brand: string;
  category: string;
  quantity: number;
  variant: string;
  metric1: string;
};

type PageType = 'cart' | 'delivery' | 'summary' | 'checkout-success-page';

type GtmEvent = {
  event: string;
  pageType: any;
  ecommerce: any;
  side?: string;
};

function formatVariant(variant: Variant) {
  return {
    id: variant.articleNumber,
    name: variant.product?.name,
    variant: variant.name,
    brand: variant?.brands?.[0]?.brand,
    price: variant?.price?.netPrice,
    quantity: +variant.quantity || 1,
    category: variant.product?.categoryPaths?.[0],
    metric1: variant?.price?.salesPrice,
  };
}

function formatProduct(product: Product) {
  return {
    id: product.id,
    name: product.name,
    category: product.categoryPaths?.[0],
    brand: product.variants[0]?.brands?.[0]?.brand || null,
  };
}

function formatCartItem(item: Variant): FormattedCartItem {
  return {
    name: item.product?.name,
    id: item.articleNumber,
    price: (+item.price?.netPrice + +item.price?.alloyingPrice).toFixed(2),
    brand: item.brands?.[0]?.brand,
    category: item.product?.categoryPaths?.[0],
    quantity: item.quantity,
    variant: item.name,
    metric1: item.price?.salesPrice,
  };
}

function totalPrice(items: FormattedCartItem[]) {
  return items.reduce((acc, curr) => +acc + +curr.price * +curr.quantity, 0).toFixed(2);
}

function totalQuantity(items: FormattedCartItem[]) {
  return items.reduce((total, product) => +total + (+product.quantity ?? 0), 0);
}

export function createListeners(gtm: GtmPlugin) {
  return {
    cartView(cartItems: CartItem[]) {
      gtm.trackEvent({
        event: 'view_cart',
        pageType: GTM_STRINGS.CART_FLYOUT,
        ecommerce: {
          currencyCode: 'SEK',
          view: {
            actionField: { list: GTM_STRINGS.CART_FLYOUT_LIST },
            products: cartItems.map((cartItem) => ({
              name: cartItem.product?.name,
              id: cartItem.articleNumber,
              price: (+cartItem.price + +cartItem.alloyingPrice).toFixed(2),
              brand: cartItem.brands?.[0]?.brand,
              category: cartItem.product?.breadcrumb?.parents?.[0]?.name,
              quantity: cartItem.quantity,
              variant: cartItem.name,
              metric1: cartItem.salesPrice,
            })),
          },
        },
      });
    },

    addToCart({
      items: cartItems,
      pageType,
      list = GTM_STRINGS.UNKNOWN_LIST,
    }: {
      items: Variant[];
      pageType: string;
      list: string;
    }) {
      const products = cartItems.map(formatCartItem);

      gtm.trackEvent({
        event: 'add_to_cart',
        pageType,
        ecommerce: {
          currencyCode: 'SEK',
          cart: {
            total: totalPrice(products),
            quantity: totalQuantity(products),
            products,
          },
          add: {
            actionField: {
              list,
            },
            products,
          },
        },
      });
    },

    removeFromCart({
      items,
      pageType,
      list = GTM_STRINGS.CART_LIST,
    }: {
      items: Variant[];
      pageType: PageType;
      list: string;
    }) {
      gtm.trackEvent({
        event: 'remove_from_cart',
        pageType,
        ecommerce: {
          currencyCode: 'SEK',
          remove: {
            actionField: {
              list,
            },
            products: items.map(formatCartItem),
          },
        },
      });
    },

    promotionImpression({ banner, pageType, position }: { banner: Banner; pageType: string; position: string }) {
      gtm.trackEvent({
        event: 'view_promotion',
        pageType,
        ecommerce: {
          promoView: {
            promotions: [
              {
                id: banner.marketingId,
                name: banner.metaTitle,
                creative: banner.creative,
                position,
              },
            ],
          },
        },
      });
    },

    promotionClick({ banner, pageType, position }: { banner: Banner; pageType: string; position: string }) {
      gtm.trackEvent({
        event: 'select_promotion',
        pageType,
        ecommerce: {
          promoClick: {
            promotions: [
              {
                id: banner.marketingId,
                name: banner.metaTitle,
                creative: banner.creative,
                position,
              },
            ],
          },
        },
      });
    },

    productClick({
      product,
      pageType,
      list = GTM_STRINGS.UNKNOWN_LIST,
    }: {
      product: Product;
      pageType: string;
      list: string;
    }) {
      gtm.trackEvent({
        event: 'select_item',
        pageType,
        ecommerce: {
          currencyCode: 'SEK',
          click: {
            actionField: { list },
            products: [formatProduct(product)],
          },
        },
      });
    },

    variantClick({
      variant,
      pageType,
      list = GTM_STRINGS.UNKNOWN_LIST,
    }: {
      variant: Variant;
      pageType: string;
      list: string;
    }) {
      gtm.trackEvent({
        event: 'select_item',
        pageType,
        ecommerce: {
          currencyCode: 'SEK',
          click: {
            actionField: { list },
            products: [formatVariant(variant)],
          },
        },
      });
    },

    productDetailView(product: Product) {
      gtm.trackEvent({
        event: 'view_item',
        pageType: GTM_STRINGS.PRODUCT_DETAIL_PAGE,
        ecommerce: {
          currencyCode: 'SEK',
          detail: {
            actionField: {},
            products: [
              {
                id: product.requestedVariant?.articleNumber,
                name: product.name,
                variant: product.requestedVariant?.name,
                brand: product.requestedVariant?.brands?.[0]?.brand,
                price: product.requestedVariant?.price?.netPrice,
                metric1: product.requestedVariant?.price?.salesPrice,
                category: product.categoryPaths?.[0],
              },
            ],
          },
        },
      });
    },

    checkout({ cartItems = [], pageType }: { cartItems: Variant[]; pageType: PageType }) {
      const products = cartItems.map(formatCartItem);

      const steps: Record<PageType, number> = {
        cart: 1,
        delivery: 2,
        summary: 3,
        'checkout-success-page': 4,
      };

      gtm.trackEvent({
        event: 'checkout',
        pageType,

        ecommerce: {
          currencyCode: 'SEK',
          checkout: {
            actionField: {
              step: steps[pageType],
            },
            products,
          },
        },
      });
    },

    purchase(orderId: string, orderItems: Variant[], affiliation = '') {
      const products = orderItems.map(formatCartItem);

      gtm.trackEvent({
        event: 'purchase',
        pageType: GTM_STRINGS.CHECKOUT_SUCCESS_PAGE,
        ecommerce: {
          currencyCode: 'SEK',
          purchase: {
            actionField: {
              affiliation,
              id: orderId,
              revenue: totalPrice(products),
              tax: 0,
              shipping: 0,
            },
            products,
          },
        },
      });
    },

    productImpression({
      products,
      side,
      list = GTM_STRINGS.UNKNOWN_LIST,
    }: {
      products: Product[];
      side?: string;
      list: string;
    }) {
      if (products?.length) {
        const route = useRoute();
        const event: GtmEvent = {
          event: 'view_item_list',
          pageType: route.name,
          ecommerce: {
            currencyCode: 'SEK',
            impressions: products.map((product, i) => {
              return { ...formatProduct(product), list, position: product.listPosition || i + 1 };
            }),
          },
        };
        if (side) {
          event.side = side;
        }
        gtm.trackEvent(event);
      }
    },

    variantImpression({ variants, list = GTM_STRINGS.UNKNOWN_LIST }: { variants: Variant[]; list: string }) {
      if (variants?.length) {
        const route = useRoute();
        gtm.trackEvent({
          event: 'view_item_list',
          pageType: route.name,
          ecommerce: {
            currencyCode: 'SEK',
            impressions: variants.map((variant, i) => {
              return {
                ...formatVariant(variant),
                list,
                position: variant.listPosition || i + 1,
              };
            }),
          },
        });
      }
    },
  };
}
